<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import type { UserInfoType } from '@/types/user'
import { socketEmitAsync } from '@/utils/web-socket'
import { Edit, EditPen, Rank, CopyDocument } from '@element-plus/icons-vue'
import { ref } from 'vue'
import { cloneDeep } from 'lodash'
import { VueDraggable } from 'vue-draggable-plus'

const userStore = useUserStore()
const sortDialogVisible = ref(false)

const props = withDefaults(
  defineProps<{
    showEditor: boolean
  }>(),
  {
    showEditor: true,
  },
)

async function handleChangeRoutine(id: number) {
  const userAttr = await socketEmitAsync<UserInfoType['userAttr']>({ type: 'useEquipRoutine', data: { id } })
  userStore.handleChangeEquipRoutine(userAttr, id)
}
async function handleNameRoutine(id: number) {
  const routine = userStore.equippedRoutineList.find((item) => item.id === id)
  if (!routine) return
  ElMessageBox.prompt('请输入装备套路名称', '修改装备套路名称', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputPattern: /^[\u4e00-\u9fa5a-zA-Z0-9]{1,8}$/,
    inputValue: routine.name,
    inputErrorMessage: '套路命名在1~8字，且只允许中英文及数字',
    confirmButtonClass: 'is-plain',
    async beforeClose(action, instance, done) {
      if (action !== 'confirm') return done()
      try {
        instance.confirmButtonLoading = true
        instance.confirmButtonText = '加载中...'
        const value = instance.inputValue
        await socketEmitAsync({ type: 'nameEquipRoutine', data: { id, name: value } })
        routine.name = value
        done()
      } finally {
        instance.confirmButtonLoading = false
        instance.confirmButtonText = '确定'
      }
    },
  })
}
async function onDragEnd() {
  const idList = userStore.equippedRoutineList.map((item) => item.id)
  await socketEmitAsync({ type: 'sortEquipRoutine', data: { idList } })
  ElMessage.success('排序成功')
}
async function handleCopyRoutine(id: number) {
  const sourceRoutine = userStore.equippedRoutineList.find((item) => item.id === id)
  if (!sourceRoutine) return
  const currentRoutine = userStore.equippedRoutineList.find((item) => item.id === userStore.equippedRoutineId)
  if (!currentRoutine) return
  await ElMessageBox.confirm(`是否将【${sourceRoutine.name}】套路复制到当前使用的【${currentRoutine.name}】套路当中？`, '复制装备套路', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    confirmButtonClass: 'is-plain',
  })
  const userAttr = await socketEmitAsync<UserInfoType['userAttr']>({ type: 'copyEquipRoutine', data: { id } })
  // 同步更新当前套路的装备数据
  const target = userStore.equippedRoutineList.find((item) => item.id === userStore.equippedRoutineId)
  if (target) {
    target.equippedList = cloneDeep(sourceRoutine.equippedList)
    target.runeEquippedList = sourceRoutine.runeEquippedList ? [...sourceRoutine.runeEquippedList] : ['', '', '', '', '']
    target.relicEquippedList = sourceRoutine.relicEquippedList ? [...sourceRoutine.relicEquippedList] : ['', '', '', '', '']
    target.skillEquippedList = sourceRoutine.skillEquippedList ? [...sourceRoutine.skillEquippedList] : ['', '', '', '']
    target.fightPetId = sourceRoutine.fightPetId
  }
  userStore.handleChangeEquipRoutine(userAttr, userStore.equippedRoutineId)
  ElMessage.success('复制成功')
}
</script>

<template>
  <div class="equip-routine" v-bind="$attrs">
    <el-select size="small" v-model="userStore.equippedRoutineId" class="routine-select" @change="handleChangeRoutine">
      <el-option v-for="item in userStore.equippedRoutineList" :key="item.id" :label="item.name" :value="item.id">
        <div class="routine-name-wrap">
          <span class="routine-name">{{ item.name }}</span>
        </div>
      </el-option>
    </el-select>
    <el-button plain size="small" class="routine-sort-btn" @click="sortDialogVisible = true" style="width: 30px" v-show="showEditor">
      <el-icon><EditPen /></el-icon>
    </el-button>
  </div>

  <el-dialog v-model="sortDialogVisible" title="编辑装备套路" width="100%" style="max-width: 420px">
    <VueDraggable v-model="userStore.equippedRoutineList" :animation="150" handle=".drag-handle" @end="onDragEnd">
      <div v-for="item in userStore.equippedRoutineList" :key="item.id" class="routine-option">
        <span class="routine-name">{{ item.name }}</span>
        <el-tag size="small" type="success" effect="plain" v-if="item.id === userStore.equippedRoutineId">使用中</el-tag>
        <el-icon v-if="item.id !== userStore.equippedRoutineId" size="20" class="routine-action" @click.stop="handleCopyRoutine(item.id)"
          ><CopyDocument
        /></el-icon>
        <el-icon size="20" class="routine-action" @click.stop="handleNameRoutine(item.id)"><Edit /></el-icon>
        <el-icon class="drag-handle" size="20"><Rank /></el-icon>
      </div>
    </VueDraggable>
    <template #footer>
      <el-button plain size="small" @click="sortDialogVisible = false">关闭</el-button>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.equip-routine {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
.routine-select {
  flex: 1;
}
.routine-sort-btn {
  flex-shrink: 0;
}
.routine-option {
  display: flex;
  align-items: center;
  font-size: 14px;
  gap: 6px;
  padding: 10px 0;
  border-bottom: 1px solid var(--el-border-color-lighter);
}
.routine-name-wrap {
  display: flex;
  align-items: center;
  font-size: 12px;
  gap: 6px;
}
.routine-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drag-handle {
  cursor: grab;
  color: var(--el-text-color-placeholder);
  touch-action: none;
  &:active {
    cursor: grabbing;
  }
}
.routine-action {
  color: var(--el-text-color-secondary);
  flex-shrink: 0;
}
</style>
